import React, { useEffect, useState, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  CheckCircle2, XCircle, AlertTriangle, Play, RefreshCw, Clock, ArrowLeft,
  Building2, Shield, Lock, ShieldOff, GitBranch, Database,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TEST_META = {
  company_profile:  { icon: Building2, label: 'Company Profile' },
  zatca_chain:      { icon: Shield,    label: 'ZATCA Chain' },
  period_lock:      { icon: Lock,      label: 'Period Lock' },
  security_guards:  { icon: ShieldOff, label: 'Security Guards' },
  workflow_engine:  { icon: GitBranch, label: 'Workflow Engine' },
  data_integrity:   { icon: Database,  label: 'Data Integrity' },
};

const STATUS_BADGE = {
  PRODUCTION_READY: {
    colour: 'emerald', label: 'PRODUCTION READY', icon: CheckCircle2,
    bg: 'from-emerald-50 to-emerald-100 border-emerald-300',
    icon_bg: 'bg-emerald-200', icon_text: 'text-emerald-800',
    title: 'text-emerald-900', sub: 'text-emerald-700', score: 'text-emerald-700',
    chip: 'bg-emerald-100 text-emerald-800',
  },
  DEGRADED: {
    colour: 'amber', label: 'DEGRADED', icon: AlertTriangle,
    bg: 'from-amber-50 to-amber-100 border-amber-300',
    icon_bg: 'bg-amber-200', icon_text: 'text-amber-800',
    title: 'text-amber-900', sub: 'text-amber-700', score: 'text-amber-700',
    chip: 'bg-amber-100 text-amber-800',
  },
  NOT_READY: {
    colour: 'rose', label: 'NOT READY', icon: XCircle,
    bg: 'from-rose-50 to-rose-100 border-rose-300',
    icon_bg: 'bg-rose-200', icon_text: 'text-rose-800',
    title: 'text-rose-900', sub: 'text-rose-700', score: 'text-rose-700',
    chip: 'bg-rose-100 text-rose-800',
  },
};

const SmokeTest = () => {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadHistory = useCallback(async () => {
    try {
      const h = await axiosInstance.get('/smoke-test/history');
      setHistory(h.data.runs || []);
    } catch (err) {
      console.warn('history load failed', err);
    }
  }, []);

  const loadLast = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axiosInstance.get('/smoke-test/last-report');
      if (r.data && r.data.id) setReport(r.data);
    } catch (err) {
      // it's ok if there's no report yet
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      await loadLast();
      await loadHistory();
    };
    init();
  }, [loadLast, loadHistory]);

  const runTests = async () => {
    setRunning(true);
    setReport(null);
    try {
      const r = await axiosInstance.post('/smoke-test/run');
      setReport(r.data);
      if (r.data.overall_status === 'PRODUCTION_READY') {
        toast.success('All tests passed — Production Ready!');
      } else if (r.data.overall_status === 'DEGRADED') {
        toast.warning(`${r.data.summary.failed} test(s) failed — see details`);
      } else {
        toast.error('Multiple tests failed — system NOT ready');
      }
      loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to run smoke tests');
    } finally {
      setRunning(false);
    }
  };

  if (loading && !report) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const badge = report ? STATUS_BADGE[report.overall_status] : null;
  const BadgeIcon = badge?.icon;

  return (
    <div className="space-y-5 max-w-6xl mx-auto" data-testid="smoke-test-page">
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/settings')}
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
          data-testid="back-to-settings"
        >
          <ArrowLeft className="w-4 h-4" /> Settings
        </button>
        <button
          onClick={runTests}
          disabled={running}
          data-testid="run-smoke-test-btn"
          className={`px-5 py-2.5 rounded-lg font-bold text-sm text-white inline-flex items-center gap-2 ${
            running
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'
          }`}
        >
          {running ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {running ? 'Running…' : 'Run Smoke Tests'}
        </button>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-gray-900">Production Smoke Test</h1>
        <p className="text-sm text-gray-500 mt-1">
          Six-stage post-deploy validation: Company Profile · ZATCA Chain · Period Lock · Security · Workflow · Data Integrity.
          Runs in &lt; 60 seconds.
        </p>
      </div>

      {report ? (
        <>
          {/* Status banner */}
          <div className={`bg-gradient-to-r ${badge.bg} border-2 rounded-xl p-6 flex items-center justify-between`} data-testid="status-banner">
            <div className="flex items-center gap-4">
              <div className={`p-4 rounded-full ${badge.icon_bg}`}>
                <BadgeIcon className={`w-10 h-10 ${badge.icon_text}`} />
              </div>
              <div>
                <h2 className={`text-2xl font-bold ${badge.title}`} data-testid="overall-status">
                  {badge.label}
                </h2>
                <p className={`text-sm ${badge.sub} mt-1`}>
                  {report.summary.passed} of {report.summary.total} tests passed
                  · Completed in {report.duration_ms}ms
                  · {new Date(report.executed_at).toLocaleString()}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className={`text-5xl font-bold ${badge.score}`}>
                {report.summary.passed}<span className="text-2xl text-gray-400">/{report.summary.total}</span>
              </div>
              <div className="text-xs text-gray-600 mt-1">Passed</div>
            </div>
          </div>

          {/* Tests grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="tests-grid">
            {report.tests.map((t) => {
              const meta = TEST_META[t.name] || { icon: CheckCircle2, label: t.name };
              const Icon = meta.icon;
              const pass = t.status === 'PASS';
              return (
                <div
                  key={t.name}
                  data-testid={`test-${t.name}`}
                  className={`bg-white rounded-xl border-2 p-4 ${pass ? 'border-emerald-200' : 'border-rose-300'}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`p-2 rounded-lg ${pass ? 'bg-emerald-50' : 'bg-rose-50'}`}>
                        <Icon className={`w-4 h-4 ${pass ? 'text-emerald-700' : 'text-rose-700'}`} />
                      </div>
                      <h3 className="font-semibold text-gray-900 text-sm">{meta.label}</h3>
                    </div>
                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${pass ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                      {pass ? '✓ PASS' : '✗ FAIL'}
                    </span>
                  </div>

                  {/* Test-specific details */}
                  <div className="text-xs text-gray-600 space-y-0.5 mt-2 font-mono">
                    {Object.entries(t)
                      .filter(([k]) => !['name', 'status', 'duration_ms', 'reason'].includes(k))
                      .map(([k, v]) => (
                        <div key={k} className="flex justify-between">
                          <span>{k}:</span>
                          <span className="truncate ml-2 text-gray-800">{String(v).slice(0, 60)}</span>
                        </div>
                      ))}
                    {t.reason && (
                      <div className="bg-rose-50 border border-rose-200 rounded p-2 text-rose-700 mt-2 font-normal break-words">
                        {t.reason}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end items-center mt-2 text-xs text-gray-500">
                    <Clock className="w-3 h-3 mr-1" /> {t.duration_ms}ms
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-12 text-center text-gray-500" data-testid="no-report">
          <Play className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p className="font-medium">No smoke test has been run yet.</p>
          <p className="text-sm mt-1">Click &ldquo;Run Smoke Tests&rdquo; above to validate the deployment.</p>
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="history-section">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-800 text-sm">Recent Runs (last 20)</h3>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left">When</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Passed</th>
                <th className="px-3 py-2 text-right">Duration</th>
              </tr>
            </thead>
            <tbody>
              {history.slice(0, 20).map((h) => {
                const b = STATUS_BADGE[h.overall_status] || STATUS_BADGE.NOT_READY;
                return (
                  <tr key={h.id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2">{new Date(h.executed_at).toLocaleString()}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${b.chip}`}>
                        {b.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{h.summary?.passed}/{h.summary?.total}</td>
                    <td className="px-3 py-2 text-right font-mono">{h.duration_ms}ms</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SmokeTest;
